import PropTypes from 'prop-types';
import pick from 'lodash/pick';
import { Button } from 'semantic-ui-react';
import { v4 as uuid } from 'uuid';

import config from '@plone/volto/registry';
import { BlocksForm } from '@plone/volto/components/manage/Form';
import EditBlockWrapper from '@plone/volto/components/manage/Blocks/Container/EditBlockWrapper';

const ALLOWED_BLOCKS = ['csjCard', 'slate', 'csjIconCardGrid', 'image'];

const getNestedBlocksConfig = () =>
  pick(config.blocks.blocksConfig, ALLOWED_BLOCKS);

const resolveColumnSelectedBlock = (blocks, layoutItems, selectedId) => {
  if (!selectedId) {
    return null;
  }
  if (layoutItems.includes(selectedId)) {
    return selectedId;
  }
  return (
    layoutItems.find((id) => {
      const nestedBlock = blocks[id];
      return (
        nestedBlock?.['@type'] === 'csjIconCardGrid' &&
        (nestedBlock.blocks_layout?.items || []).includes(selectedId)
      );
    }) || null
  );
};

const ColumnBlocksEditor = ({
  columnId,
  columnLabel,
  data,
  block,
  onChangeBlock,
  pathname,
  selected,
  manage,
  metadata,
  properties,
  gridSelected,
  setGridSelected,
  defaultChildVariant = 'icon',
}) => {
  const blocksKey = `${columnId}_blocks`;
  const layoutKey = `${columnId}_blocks_layout`;
  const columnBlocks = data[blocksKey] || {};
  const columnLayout = data[layoutKey] || { items: [] };
  const blocksConfig = getNestedBlocksConfig();
  const selectedBlockInColumn = selected
    ? resolveColumnSelectedBlock(columnBlocks, columnLayout.items, gridSelected)
    : null;

  const onAddBlock = () => {
    const newBlockId = uuid();
    onChangeBlock(block, {
      ...data,
      [blocksKey]: {
        ...columnBlocks,
        [newBlockId]: {
          '@type': 'csjCard',
          variant: defaultChildVariant,
        },
      },
      [layoutKey]: {
        items: [...columnLayout.items, newBlockId],
      },
    });
    setGridSelected(newBlockId);
  };

  return (
    <div className={`column-grid-column column-grid-column-${columnId}`}>
      <div className="column-grid-column-header">
        <span>{columnLabel}</span>
        {selected ? (
          <Button compact size="mini" type="button" onClick={onAddBlock}>
            + Element
          </Button>
        ) : null}
      </div>
      <div
        className="column-grid-column-blocks"
        role="presentation"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <BlocksForm
          metadata={metadata || properties}
          properties={{
            blocks: columnBlocks,
            blocks_layout: columnLayout,
          }}
          direction="vertical"
          manage={manage}
          selectedBlock={selected ? selectedBlockInColumn : null}
          blocksConfig={blocksConfig}
          isContainer
          isMainForm={false}
          disableAddBlockOnEnterKey
          onSelectBlock={(id) => {
            setGridSelected(id);
          }}
          onChangeFormData={(newFormData) => {
            onChangeBlock(block, {
              ...data,
              [blocksKey]: newFormData.blocks,
              [layoutKey]: newFormData.blocks_layout,
            });
          }}
          onChangeField={(id, value) => {
            if (id === 'blocks' || id === 'blocks_layout') {
              onChangeBlock(block, {
                ...data,
                [blocksKey]: id === 'blocks' ? value : columnBlocks,
                [layoutKey]: id === 'blocks_layout' ? value : columnLayout,
              });
            }
          }}
          pathname={pathname}
        >
          {({ draginfo }, editBlock, blockProps) => (
            <EditBlockWrapper draginfo={draginfo} blockProps={blockProps}>
              {editBlock}
            </EditBlockWrapper>
          )}
        </BlocksForm>
      </div>
    </div>
  );
};

ColumnBlocksEditor.propTypes = {
  columnId: PropTypes.oneOf(['left', 'right']).isRequired,
  columnLabel: PropTypes.string.isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  block: PropTypes.string.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  pathname: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  manage: PropTypes.bool.isRequired,
  metadata: PropTypes.objectOf(PropTypes.any),
  properties: PropTypes.objectOf(PropTypes.any),
  gridSelected: PropTypes.string,
  setGridSelected: PropTypes.func.isRequired,
  defaultChildVariant: PropTypes.string,
};

export default ColumnBlocksEditor;
